import * as Sentry from '@sentry/react';
import {
  withSentryRouting,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  browserTracingIntegration,
  replayIntegration,
} from '@sentry/react';
import type { Analytics } from 'firebase/analytics';
import { logEvent } from 'firebase/analytics';
import { TelemetryEventName } from '@little-chef/domain';

export { withSentryRouting };

/**
 * Initialise Sentry for the browser.
 *
 * Must be called at the very top of main.tsx before ReactDOM.createRoot().
 * The analytics instance must already be initialised before calling this
 * (ensures the beforeSend cross-link can access it).
 *
 * Fail-silent: any Sentry.init error is caught and logged locally.
 */
export function initSentry(analytics: Analytics): void {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN_FRONTEND,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
      release: import.meta.env.VITE_SENTRY_RELEASE,
      transport: makeBrowserOfflineTransport(makeFetchTransport),
      integrations: [browserTracingIntegration(), replayIntegration()],
      tracesSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,

      /**
       * beforeSend cross-link (FR-007 / US5):
       * For every fatal error, fire a consent-exempt `critical_error` analytics event
       * so Sentry events can be correlated with Firebase Analytics sessions.
       *
       * Uses setTimeout(0) to avoid blocking Sentry's synchronous pipeline.
       */
      beforeSend(event) {
        if (event.level === 'fatal') {
          const sentryEventId = event.event_id ?? 'unknown';
          const errorType = event.exception?.values?.[0]?.type ?? 'unknown';

          setTimeout(() => {
            try {
              logEvent(analytics, TelemetryEventName.CRITICAL_ERROR, {
                sentry_event_id: sentryEventId,
                error_type: errorType,
              });
            } catch (e) {
              // Fail-silent: log locally, never affect the Sentry pipeline
              console.warn('[Telemetry] Failed to send critical_error cross-link', e);
            }
          }, 0);
        }
        return event;
      },
    });
  } catch (e) {
    console.warn('[Sentry] Initialisation failed', e);
  }
}
